import { useMemo, type ReactNode } from 'react';
import { ModelChip, ModelLogo, ProviderLogo, providerName } from '@shared/brand';
import { StackedBarChart, StatTile, seriesColor } from '@shared/charts';
import { compact, dateTime, idr, ms, num, percent, relative } from '@shared/format';
import { Badge, Card, CopyButton, Delta, Empty, Icon, InfoTip, KeyValue, Modal, Tabs, type IconName } from './ui';

export type Range = '24h' | '7d' | '30d' | '90d';
export type GroupBy = 'model' | 'provider' | 'api_key' | 'project';
export type Metric = 'spend_idr' | 'tokens' | 'requests';

interface GroupTotals {
  spend_idr: number;
  requests: number;
  failed_requests: number;
  input_tokens: number;
  cached_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
}

export interface Activity {
  range: Range;
  bucket: 'hour' | 'day' | 'week';
  group_by: GroupBy;
  from: string;
  to: string;
  buckets: string[];
  groups: Array<{ key: string; label: string; provider: string | null; family: string | null; totals: GroupTotals }>;
  series: Record<Metric, Record<string, number[]>>;
  totals: GroupTotals & { avg_latency_ms: number; p50_latency_ms: number; p95_latency_ms: number };
  previous: { spend_idr: number; requests: number; tokens: number };
}

export interface UsageRow {
  request_id: string;
  created_at: string;
  model: string | null;
  provider: string | null;
  display_name: string | null;
  family: string | null;
  api_key_name: string | null;
  kind: 'text' | 'image' | 'video' | null;
  status: string;
  streamed: boolean;
  input_tokens: number;
  cached_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  total_tokens: number;
  cost_idr: number;
  latency_ms: number | null;
  output_tps: number | null;
  upstream_model: string | null;
}

export const RANGES: Array<{ id: Range; label: string }> = [
  { id: '24h', label: '24 jam' },
  { id: '7d', label: '7 hari' },
  { id: '30d', label: '30 hari' },
  { id: '90d', label: '90 hari' },
];

export const METRICS: Array<{ id: Metric; label: string; icon: IconName }> = [
  { id: 'spend_idr', label: 'Biaya', icon: 'coin' },
  { id: 'tokens', label: 'Token', icon: 'bars' },
  { id: 'requests', label: 'Request', icon: 'pulse' },
];

export const STATUS_LABELS: Partial<Record<string, string>> = {
  PENDING: 'Menunggu',
  STREAMING: 'Berjalan',
  COMPLETED: 'Selesai',
  FAILED: 'Gagal',
  CANCELLED: 'Dibatalkan',
};

const KIND_ICON: Record<string, IconName> = { text: 'chat', image: 'image', video: 'video' };
const KIND_LABEL: Record<string, string> = { text: 'Chat', image: 'Gambar', video: 'Video' };

export const PAGE_SIZE = 20;
const TZ = 'Asia/Jakarta';

const TIPS = {
  tokensIn: 'Token dari prompt Anda: instruksi sistem, riwayat percakapan, dan pesan baru. Termasuk token yang dibaca dari cache.',
  tokensOut: 'Token jawaban model, termasuk token reasoning pada model yang berpikir dulu sebelum menjawab.',
  latency: (
    <>
      Lama satu request dari dikirim sampai selesai. <b>p50</b>: separuh request lebih cepat dari angka ini. <b>p95</b>: 95% request
      lebih cepat; sisanya yang paling lambat.
    </>
  ),
  tps: 'Token per detik: kecepatan model menulis jawaban setelah token pertama keluar.',
  share: 'Porsi biaya grup ini dari total biaya di rentang yang dipilih.',
} as const;

function Tip({ children, tip, label }: { children: ReactNode; tip: ReactNode; label: string }) {
  return (
    <span className="label-tip">
      {children}
      <InfoTip label={label}>{tip}</InfoTip>
    </span>
  );
}

function bucketLabel(iso: string, bucket: Activity['bucket']): string {
  const date = new Date(iso);
  if (bucket === 'hour') return new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: TZ }).format(date);
  const day = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', timeZone: TZ }).format(date);
  return bucket === 'week' ? `mg ${day}` : day;
}

function tokensOf(t: GroupTotals): number {
  return t.input_tokens + t.cached_tokens + t.output_tokens + t.reasoning_tokens;
}

function formatMetric(metric: Metric, value: number): string {
  if (metric === 'spend_idr') return idr(value);
  return compact(value);
}

function bucketWord(bucket: Activity['bucket']): string {
  return bucket === 'hour' ? 'jam' : bucket === 'week' ? 'minggu' : 'hari';
}

/** Stable group colours. */
export function useGroupColors(activity: Activity | null): (key: string) => string {
  return useMemo(() => {
    const order = new Map((activity?.groups ?? []).map((g, i) => [g.key, i]));
    return (key: string) => (key === 'other' ? 'var(--series-8)' : seriesColor(order.get(key) ?? 0));
  }, [activity]);
}

function GroupLabel({ group, by }: { group: Activity['groups'][number]; by: GroupBy }) {
  if (group.key === 'other') {
    return (
      <span className="model-chip">
        <span className="logo" style={{ width: 24, height: 24 }}>
          <Icon name="grid" size={14} />
        </span>
        <span className="model-name">{group.label}</span>
      </span>
    );
  }
  if (by === 'model') return <ModelChip model={group.key} name={group.label} provider={group.provider} family={group.family} size={24} />;
  if (by === 'provider') {
    return (
      <span className="model-chip">
        <ProviderLogo provider={group.key} size={24} />
        <span className="model-name">{providerName(group.key)}</span>
      </span>
    );
  }
  return (
    <span className="model-chip">
      <span className="logo" style={{ width: 24, height: 24 }}>
        <Icon name={by === 'api_key' ? 'key' : 'folder'} size={14} />
      </span>
      <span className="model-name">{group.label}</span>
    </span>
  );
}

function totalSeries(activity: Activity, m: Metric): number[] {
  return activity.buckets.map((_, i) => activity.groups.reduce((sum, g) => sum + (activity.series[m][g.key]?.[i] ?? 0), 0));
}

export function UsageTiles({ activity, periodLabel }: { activity: Activity; periodLabel: string }) {
  const t = activity.totals;
  const prev = activity.previous;
  const tokens = tokensOf(t);
  const success = t.requests ? (t.requests - t.failed_requests) / t.requests : 1;
  const none = t.requests === 0;
  // no "Baru" on zero
  const delta = (current: number, previous: number, invert = false) =>
    current || previous ? <Delta current={current} previous={previous} invert={invert} /> : undefined;
  return (
    <div className="grid grid-4 mb">
      <StatTile
        label="Biaya"
        value={idr(t.spend_idr)}
        accent
        delta={delta(t.spend_idr, prev.spend_idr, true)}
        sub={`${periodLabel} terakhir`}
        spark={none ? undefined : totalSeries(activity, 'spend_idr')}
      />
      <StatTile
        label="Request"
        value={num(t.requests)}
        delta={delta(t.requests, prev.requests)}
        sub={none ? 'Belum ada request' : `${num(t.requests - t.failed_requests)} berhasil · ${num(t.failed_requests)} gagal${t.failed_requests ? ` (${percent(success * 100)} berhasil)` : ''}`}
        spark={none ? undefined : totalSeries(activity, 'requests')}
      />
      <StatTile
        label={
          <Tip label="Tentang token" tip={<>Satuan teks yang dihitung model, kira-kira ¾ kata. <b>Masuk</b>: prompt Anda. <b>Keluar</b>: jawaban model.</>}>
            Token
          </Tip>
        }
        value={compact(tokens)}
        delta={delta(tokens, prev.tokens)}
        sub={none ? 'Belum ada token terpakai' : `${compact(t.input_tokens + t.cached_tokens)} masuk · ${compact(t.output_tokens + t.reasoning_tokens)} keluar`}
        spark={none ? undefined : totalSeries(activity, 'tokens')}
      />
      <StatTile
        label={
          <Tip label="Tentang latensi" tip={TIPS.latency}>
            Latensi p50
          </Tip>
        }
        value={none ? '—' : ms(t.p50_latency_ms)}
        sub={none ? 'Tampil setelah ada request' : `p95 ${ms(t.p95_latency_ms)} · rata-rata ${ms(t.avg_latency_ms)}`}
      />
    </div>
  );
}

export function ActivityChart({
  activity,
  metric,
  onMetric,
  colorFor,
  periodLabel,
  loading,
  className = 'mb',
}: {
  activity: Activity;
  metric: Metric;
  onMetric: (metric: Metric) => void;
  colorFor: (key: string) => string;
  periodLabel: string;
  loading?: boolean;
  className?: string;
}) {
  const t = activity.totals;
  const total = metric === 'spend_idr' ? t.spend_idr : metric === 'tokens' ? tokensOf(t) : t.requests;
  const data = useMemo(
    () =>
      activity.buckets.map((bucket, i) => ({
        label: bucketLabel(bucket, activity.bucket),
        segments: activity.groups.map((g) => ({ key: g.key, label: g.label, value: activity.series[metric][g.key]?.[i] ?? 0 })),
      })),
    [activity, metric],
  );
  return (
    <Card
      className={className}
      title={`${METRICS.find((m) => m.id === metric)?.label} per ${bucketWord(activity.bucket)}`}
      description={
        <>
          Total {periodLabel}: <strong style={{ color: 'var(--text-primary)' }}>{formatMetric(metric, total)}</strong>
          {loading && <span className="spinner" style={{ marginLeft: 8 }} />}
        </>
      }
      action={<Tabs tabs={METRICS} value={metric} onChange={onMetric} tight />}
    >
      <StackedBarChart
        data={data}
        height={260}
        colorFor={(key) => colorFor(key)}
        formatValue={(v) => formatMetric(metric, v)}
        emptyMessage="Belum ada pemakaian di rentang ini"
      />
    </Card>
  );
}

export function BreakdownTable({
  activity,
  by,
  label,
  colorFor,
  onPick,
  pickTitle,
}: {
  activity: Activity;
  by: GroupBy;
  label: string;
  colorFor: (key: string) => string;
  onPick?: (key: string) => void;
  pickTitle?: string;
}) {
  const total = activity.totals.spend_idr;
  if (activity.groups.length === 0) return <Empty>Belum ada request di rentang ini.</Empty>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{label}</th>
            <th className="num">Request</th>
            <th className="num">
              <Tip label="Tentang token masuk" tip={TIPS.tokensIn}>
                Token masuk
              </Tip>
            </th>
            <th className="num">
              <Tip label="Tentang token keluar" tip={TIPS.tokensOut}>
                Token keluar
              </Tip>
            </th>
            <th className="num">Biaya</th>
            <th style={{ width: 140 }}>
              <Tip label="Tentang porsi" tip={TIPS.share}>
                Porsi
              </Tip>
            </th>
          </tr>
        </thead>
        <tbody>
          {activity.groups.map((g) => {
            const share = total ? g.totals.spend_idr / total : 0;
            const clickable = Boolean(onPick) && g.key !== 'other';
            return (
              <tr
                key={g.key}
                className={clickable ? 'clickable' : undefined}
                onClick={clickable ? () => onPick?.(g.key) : undefined}
                title={clickable ? pickTitle : undefined}
              >
                <td style={{ minWidth: 180 }}>
                  <div className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                    <span className="legend-swatch" style={{ background: colorFor(g.key) }} />
                    <GroupLabel group={g} by={by} />
                  </div>
                </td>
                <td className="num">
                  {num(g.totals.requests)}
                  {g.totals.failed_requests > 0 && <div className="usage-sub">{num(g.totals.failed_requests)} gagal</div>}
                </td>
                <td className="num">{compact(g.totals.input_tokens + g.totals.cached_tokens)}</td>
                <td className="num">{compact(g.totals.output_tokens + g.totals.reasoning_tokens)}</td>
                <td className="num strong">{idr(g.totals.spend_idr)}</td>
                <td>
                  <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                    <div className="meter grow">
                      <span style={{ width: `${Math.max(share * 100, 1)}%`, background: colorFor(g.key) }} />
                    </div>
                    <span className="muted tabular" style={{ fontSize: 12, width: 46, textAlign: 'right' }}>
                      {percent(share * 100)}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Pager({ page, pages, onPage }: { page: number; pages: number; onPage: (page: number) => void }) {
  return (
    <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
      <button className="btn btn-sm icon-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Halaman sebelumnya">
        <Icon name="left" size={15} />
      </button>
      <span className="muted tabular" style={{ fontSize: 12.5, minWidth: 48, textAlign: 'center' }}>
        {page}/{pages}
      </span>
      <button className="btn btn-sm icon-btn" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Halaman berikutnya">
        <Icon name="right" size={15} />
      </button>
    </div>
  );
}

export function RequestTable({ rows, onSelect, showKey = true }: { rows: UsageRow[]; onSelect: (row: UsageRow) => void; showKey?: boolean }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Waktu</th>
            <th>Model</th>
            <th>Jenis</th>
            {showKey && <th>Key</th>}
            <th className="num">
              <Tip label="Tentang token" tip={<>Token masuk → token keluar. {TIPS.tokensOut}</>}>
                Token
              </Tip>
            </th>
            <th className="num">Biaya</th>
            <th className="num">Latensi</th>
            <th className="num">
              <Tip label="Tentang tok/dtk" tip={TIPS.tps}>
                Tok/dtk
              </Tip>
            </th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.request_id}
              className="clickable"
              tabIndex={0}
              onClick={() => onSelect(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onSelect(row);
              }}
            >
              <td className="nowrap" title={dateTime(row.created_at)}>
                {relative(row.created_at)}
              </td>
              <td style={{ minWidth: 160 }}>
                {row.model ? (
                  <ModelChip model={row.model} name={row.display_name} provider={row.provider} family={row.family} size={22} showId={false} />
                ) : (
                  '—'
                )}
              </td>
              <td>
                <span className="tag nowrap">
                  <Icon name={KIND_ICON[row.kind ?? 'text'] ?? 'chat'} size={13} style={{ marginRight: 5 }} />
                  {KIND_LABEL[row.kind ?? 'text'] ?? row.kind}
                  {row.streamed && ' · stream'}
                </span>
              </td>
              {showKey && (
                <td className="truncate" style={{ maxWidth: 160, minWidth: 110 }}>
                  {row.api_key_name ?? '—'}
                </td>
              )}
              <td className="num nowrap">
                {row.kind === 'image' ? (
                  `${num(row.output_tokens)} gbr`
                ) : (
                  <>
                    {compact(row.input_tokens + row.cached_tokens)}
                    <span className="muted"> → </span>
                    {compact(row.output_tokens + row.reasoning_tokens)}
                  </>
                )}
              </td>
              <td className="num strong nowrap">{idr(row.cost_idr, true)}</td>
              <td className="num nowrap">{ms(row.latency_ms)}</td>
              <td className="num">{row.output_tps === null ? '—' : num(Math.round(row.output_tps))}</td>
              <td>
                <Badge status={row.status}>{STATUS_LABELS[row.status] ?? row.status}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RequestDetailModal({ row, onClose }: { row: UsageRow; onClose: () => void }) {
  return (
    <Modal title="Detail request" onClose={onClose} width={560}>
      <div className="row mb" style={{ gap: 12, flexWrap: 'nowrap' }}>
        <ModelLogo model={row.model} family={row.family} provider={row.provider} size={40} />
        <div className="grow" style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{row.display_name ?? row.model ?? 'Model tidak dikenal'}</div>
          <div className="muted small truncate">
            {row.model} · {providerName(row.provider)}
          </div>
        </div>
        <Badge status={row.status}>{STATUS_LABELS[row.status] ?? row.status}</Badge>
      </div>
      <KeyValue
        items={[
          [
            'Request ID',
            <span className="row" style={{ gap: 8 }}>
              <code>{row.request_id}</code>
              <CopyButton value={row.request_id} />
            </span>,
          ],
          ['Waktu', dateTime(row.created_at)],
          ['Jenis', `${KIND_LABEL[row.kind ?? 'text'] ?? row.kind}${row.streamed ? ' (streaming)' : ''}`],
          ['Versi di provider', row.upstream_model ? <code>{row.upstream_model}</code> : '—'],
          ['API key', row.api_key_name ?? '—'],
          ['Token masuk', `${num(row.input_tokens)}${row.cached_tokens ? ` + ${num(row.cached_tokens)} dari cache` : ''}`],
          ['Token keluar', `${num(row.output_tokens)}${row.reasoning_tokens ? ` + ${num(row.reasoning_tokens)} reasoning` : ''}`],
          ['Biaya', <strong>{idr(row.cost_idr, true)}</strong>],
          ['Latensi', ms(row.latency_ms)],
          ['Kecepatan', row.output_tps === null ? '—' : `${num(Math.round(row.output_tps))} token/detik`],
        ]}
      />
      <p className="field-hint mt" style={{ marginBottom: 0 }}>
        Sertakan request ID ini saat menghubungi tim kami agar kasusnya bisa dilacak.
      </p>
    </Modal>
  );
}
